import { SOLUTION_SOURCES } from '../../shared/solution-source.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import * as crypto from 'node:crypto';
import { Exam, ExamTask } from '../exam.js';
import type { BoundingBox } from '../BoundingBox.js';

/**
 * Aggregate OCR text from all boundingBoxes
 * @param boundingBoxes Array of BoundingBox objects with ocrExtractedText
 * @returns Concatenated OCR text, or empty string if no boxes
 */
export function aggregateOCRFromBoundingBoxes(boundingBoxes: BoundingBox[] | undefined): string {
  if (!boundingBoxes || !Array.isArray(boundingBoxes) || boundingBoxes.length === 0) {
    return '';
  }
  return boundingBoxes
    .map(bbox => (bbox.ocrExtractedText ?? '').trim())
    .filter(text => text.length > 0)
    .join(' ');
}

export async function addExamTasks(
  examId: string,
  tasks: { taskNumber: string; description: string }[],
): Promise<number> {
  const examCollection = await getCollection(Exam as any);

  // Read existing tasks directly from Mongo (projection)
  const existingExam = await examCollection.findOne({ _id: examId } as any, { projection: { tasks: 1 } });
  const existingTasks: string[] = Array.isArray(existingExam?.tasks)
    ? existingExam.tasks.map((t: any) => t.taskNumber)
    : [];

  // Determine which tasks to create (idempotent)
  const toCreate = tasks.map(t => t.taskNumber).filter(tn => !existingTasks.includes(tn));
  if (toCreate.length === 0) return 0;

  for (const taskNumber of toCreate) {
    const description = tasks.find(t => t.taskNumber === taskNumber)?.description ?? '';
    const examTask = new ExamTask({ _id: crypto.randomUUID(), taskNumber, description });

    // Ensure tasks field exists as an array if it was null
    await examCollection.updateOne({ _id: examId, $or: [{ tasks: { $exists: false } }, { tasks: null }] } as any, {
      $set: { tasks: [] },
    });

    await examCollection.updateOne({ _id: examId } as any, { $push: { tasks: examTask.toObject() } });
  }

  return toCreate.length;
}

export default addExamTasks;

/**
 * Maps raw DB exam tasks (field `subTasks`) to a recursive tree (field `subtasks`) with all
 * detail fields needed by the extraction pipeline. Used wherever persisted tasks must be fed
 * back into an agent expecting the intermediate tree shape.
 */
export function examTaskTreeFromDB(nodes: any[]): any[] {
  if (!Array.isArray(nodes)) return [];
  return nodes
    .map(node => {
      const taskNumber = String(node?.taskNumber ?? '').trim();
      if (!taskNumber) return null;
      // Model C points round-trip (must mirror the editor's flatten so post-confirm MS/GS rebuilds preserve
      // the confirmed points): the transport carries the declared TOTAL in `maxPoints` (a leaf falls back to
      // its scheme points) and the parent's OWN answer in `ownMaxPoints` (from `gradingScheme.maxPoints`).
      // (Reading own into `maxPoints` and dropping the total — the old behavior — corrupted every rebuild.)
      const hasKids = Array.isArray(node?.subTasks) && node.subTasks.length > 0;
      const schemeMaxPoints =
        typeof node?.gradingScheme?.maxPoints === 'number' ? Number(node.gradingScheme.maxPoints) : undefined;
      const declaredTotal =
        typeof node?.declaredMaxPoints === 'number'
          ? Number(node.declaredMaxPoints)
          : !hasKids
            ? schemeMaxPoints
            : undefined;
      return {
        // Identity has to survive the round-trip: build-exam-tasks reuses `_id` when it is there and mints a
        // fresh uuid when it is not, so dropping it here silently regenerated every task's id on every MS/GS
        // extraction — orphaning anything keyed on it (progress units, canvas selection).
        _id: typeof node?._id === 'string' && node._id.trim() ? node._id : undefined,
        taskNumber,
        description: typeof node?.description === 'string' ? node.description : '',
        taskType: typeof node?.taskType === 'string' ? node.taskType : undefined,
        // Carried through so a rebuild can respect the stored value. Today `needsAnswer` is derived
        // from `taskType` and would be recomputed identically anyway, but the extraction steps rebuild
        // from the DB now, so the field has to survive the round-trip rather than rely on that.
        needsAnswer: typeof node?.needsAnswer === 'boolean' ? node.needsAnswer : undefined,
        descriptionBoundingBoxes: Array.isArray(node?.boundingBoxes) ? node.boundingBoxes : [],
        masterSolutionContent:
          typeof node?.masterSolution?.content === 'string' ? node.masterSolution.content : undefined,
        masterSolutionBoundingBoxes: Array.isArray(node?.masterSolution?.boundingBoxes)
          ? node.masterSolution.boundingBoxes
          : undefined,
        // Provenance has to survive this round-trip for the same reason `needsAnswer` does — the extraction
        // steps rebuild the whole tree from here, and unlike `confidence` (recomputed from the boxes) it
        // cannot be derived again. Dropping it would relabel every generated solution as uploaded.
        masterSolutionSource: SOLUTION_SOURCES.includes(node?.masterSolution?.source)
          ? node.masterSolution.source
          : undefined,
        gradingCriteria: typeof node?.gradingScheme?.criteria === 'string' ? node.gradingScheme.criteria : undefined,
        gradingBoundingBoxes: Array.isArray(node?.gradingScheme?.boundingBoxes)
          ? node.gradingScheme.boundingBoxes
          : undefined,
        gradingSchemeSource: SOLUTION_SOURCES.includes(node?.gradingScheme?.source)
          ? node.gradingScheme.source
          : undefined,
        maxPoints: declaredTotal,
        ownMaxPoints: hasKids ? schemeMaxPoints : undefined,
        additionalInfoIds: Array.isArray(node?.additionalInfoIds) ? node.additionalInfoIds : [],
        inlineFigureIds: Array.isArray(node?.inlineFigureIds) ? node.inlineFigureIds : [],
        subtasks: examTaskTreeFromDB(node?.subTasks ?? []),
      };
    })
    .filter(Boolean);
}

/**
 * Strips an exam task tree to the minimal shape passed to agent prompts:
 * `{ taskNumber, description, taskType, additionalInfoIds, subtasks }`. Accepts both the intermediate tree
 * (field `subtasks`, from `examTaskTreeFromDB` or the structure agent) and raw DB tasks
 * (field `subTasks`), so it can be called on either without a prior conversion step.
 */
export function minimalExamTaskTree(nodes: any[]): any[] {
  if (!Array.isArray(nodes)) return [];
  return nodes
    .map(node => {
      const taskNumber = String(node?.taskNumber ?? '').trim();
      if (!taskNumber) return null;
      return {
        taskNumber,
        taskType: typeof node?.taskType === 'string' ? node.taskType : undefined,
        description: typeof node?.description === 'string' ? node.description : '',
        additionalInfoIds: Array.isArray(node?.additionalInfoIds) ? node.additionalInfoIds : [],
        subtasks: minimalExamTaskTree(node?.subtasks ?? node?.subTasks ?? []),
      };
    })
    .filter(Boolean) as any[];
}

/**
 * Convenience: maps raw DB exam tasks directly to the minimal JSON string used by agents.
 */
export function buildMinimalTasksJson(dbTasks: any[]): string {
  return JSON.stringify({ tasks: minimalExamTaskTree(dbTasks) });
}

export function checkTaskNumberSetConsistency(tasksPerSubmission: string[][]) {
  // returns { consistent, referenceSet, diffs }
  if (!tasksPerSubmission || tasksPerSubmission.length === 0) {
    return { consistent: true, referenceSet: new Set<string>(), diffs: [] as any[] };
  }

  const reference = tasksPerSubmission[0];
  const referenceSet = new Set(reference);
  const diffs: Array<{ submissionIndex: number; missing: string[]; extra: string[] }> = [];

  for (let i = 0; i < tasksPerSubmission.length; i++) {
    const list = tasksPerSubmission[i] || [];
    const listSet = new Set(list);

    // missing = items in reference but not in this list
    const missing: string[] = [];
    for (const t of referenceSet) if (!listSet.has(t)) missing.push(t);

    // extra = items in list but not in reference
    const extra: string[] = [];
    for (const t of listSet) if (!referenceSet.has(t)) extra.push(t);

    diffs.push({ submissionIndex: i, missing, extra });
  }

  // consistent if all diffs have no missing and no extra
  const consistent = diffs.every(d => d.missing.length === 0 && d.extra.length === 0);
  return { consistent, referenceSet, diffs };
}

/**
 * Submissions of this exam whose answers have been extracted — the only ones there is anything to grade for.
 *
 * A submission created by the split but not yet extracted has no `taskSubmissions`, so grading it finds nothing
 * for any task while still counting toward the progress total, which then never reaches it. Every caller that
 * enumerates "who can be graded" has to make that exclusion, and each of them used to spell the predicate out
 * for itself — one copy in the grading job's units, one in the re-grade dispatcher.
 *
 * `restrictTo` narrows to a caller's own subset (a manual insight names every submission BUT the corrected one,
 * a new-submissions re-grade names only the new arrivals) while keeping the extraction filter.
 */
export async function readySubmissionIds(examId: string, restrictTo?: string[]): Promise<string[]> {
  const { Submission } = await import('../submission.js');
  const submissionCollection = await getCollection(Submission);
  const filter: any = { examId, 'taskSubmissions.0': { $exists: true } };
  if (restrictTo?.length) filter._id = { $in: restrictTo };

  const submissions = await submissionCollection
    .find(filter as any)
    .project({ _id: 1 })
    .toArray();
  return submissions.map(submission => String((submission as any)._id));
}
